import { useState } from 'react';

import toast from 'react-hot-toast';
import { Crown, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import { workspaceMembersApi } from '@/lib/api/workspace-members.api';
import type { WorkspaceMemberResponse } from '@optidev/shared-types';

interface TransferOwnershipDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: WorkspaceMemberResponse;
  workspaceName: string;
  onTransferred: () => void;
}

/**
 * Transfer Ownership Dialog
 * Destructive confirmation dialog for handing the workspace to another admin.
 * Requires typing the workspace name exactly (case-sensitive) to enable confirm.
 */
export function TransferOwnershipDialog({
  open,
  onOpenChange,
  member,
  workspaceName,
  onTransferred,
}: TransferOwnershipDialogProps) {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const memberName = member.user.displayName || member.user.email;
  const canConfirm =
    workspaceName.length > 0 && confirmText === workspaceName && !submitting;

  const handleOpenChange = (nextOpen: boolean) => {
    if (submitting) return;
    if (!nextOpen) {
      setConfirmText('');
    }
    onOpenChange(nextOpen);
  };

  const handleConfirm = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (!canConfirm) return;

    try {
      setSubmitting(true);
      await workspaceMembersApi.transferOwnership(member.user.id);
      toast.success(`Ownership transferred to ${memberName}`);
      onTransferred();
      setConfirmText('');
      onOpenChange(false);
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } };
      toast.error(
        err.response?.data?.message || 'Failed to transfer ownership',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Crown className="h-4 w-4" />
            Transfer Ownership
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-3">
              <p>
                You are about to transfer ownership of{' '}
                <strong>{workspaceName}</strong> to{' '}
                <strong>{memberName}</strong>. This action:
              </p>
              <ul className="list-disc space-y-1.5 pl-5">
                <li>
                  You will become an Admin — only {memberName} can transfer
                  ownership back
                </li>
                <li>
                  Billing responsibility and future receipts move to{' '}
                  {member.user.email}
                </li>
                <li>
                  Your payment method stays on file until {memberName} replaces
                  or keeps it
                </li>
              </ul>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2">
          <Label htmlFor="transfer-confirm-name">
            Type <strong>{workspaceName}</strong> to confirm
          </Label>
          <Input
            id="transfer-confirm-name"
            value={confirmText}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setConfirmText(event.target.value)
            }
            placeholder={workspaceName}
            autoComplete="off"
            disabled={submitting}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={submitting}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Transfer Ownership
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
